import { useState } from 'react'

import {
  Box,
  Divider,
  Text,
  Title,
  WizardStep,
  WizardStepRenderParams,
} from '@factorialco/deprecated-design-system'

import Callout from 'modules/core/components/Callout'
import DayPicker from 'modules/core/components/DayPicker'
import NumberField from 'modules/core/components/NumberField'
import SelectField from 'modules/core/components/SelectField'
import TextField from 'modules/core/components/TextField'
import { acceptedContentTypes } from 'modules/core/lib/fileFormats'
import i18n from 'modules/core/lib/i18n'

import { HealthcarePlanData } from 'modules/benefits/components/benefitModals/HealthcarePlanWizard/types'
import { planDetailsStepSchema } from 'modules/benefits/components/benefitModals/HealthcarePlanWizard/validation'
import DropzoneBox from 'modules/documents/components/Documents/DropzoneBox'

const PlanDetailsStep = ({ data, onDataChanged }: WizardStepRenderParams<HealthcarePlanData>) => {
  // Track which fields have been touched/blurred to show validation errors
  const [touchedFields, setTouchedFields] = useState<Set<string>>(new Set())

  // File upload patterns from working wizards - following exact single file pattern
  const ACCEPTED_CONTENT_TYPES = acceptedContentTypes([
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  ])

  // Following exact pattern from Employee Import wizard for single file upload
  const onDrop = (files: File[]) => {
    if (files.length > 0) {
      onDataChanged({ ...data, planDocument: files[0] })
      // Mark the file field as touched when user drops a file
      setTouchedFields((prev) => new Set(prev).add('planDocument'))
    }
  }

  // Helper to mark field as touched
  const markFieldTouched = (fieldName: string) => {
    setTouchedFields((prev) => new Set(prev).add(fieldName))
  }

  // Use Zod validation from validation.ts
  const validationResult = planDetailsStepSchema().safeParse(data)

  // Extract field-specific errors from Zod validation, but only show if field was touched
  const getFieldError = (fieldName: string): string | undefined => {
    if (validationResult.success || !touchedFields.has(fieldName)) return undefined

    const fieldError = validationResult.error.errors.find((error) => error.path.includes(fieldName))
    return fieldError?.message
  }

  const errors = {
    providerName: getFieldError('providerName'),
    planName: getFieldError('planName'),
    renewalDate: getFieldError('renewalDate'),
    waitingPeriod: getFieldError('waitingPeriodMonths'),
    allowPreExistingConditions: getFieldError('allowPreExistingConditions'),
    planDocument: getFieldError('planDocument'),
  }
  return (
    <Box flexDirection="column" gap="s24" width="s360">
      <Box flexDirection="column" gap="s8">
        <Title
          title={i18n.t('benefits.healthcare.plan_wizard.plan_details_step.title')}
          description={i18n.t('benefits.healthcare.plan_wizard.plan_details_step.description')}
        />
      </Box>

      <Box flexDirection="column" gap="s24">
        {/* Basic Information Section */}
        <Box flexDirection="column" gap="s16">
          <Title
            title={i18n.t(
              'benefits.healthcare.plan_wizard.plan_details_step.basic_information.title'
            )}
            variant="500"
          />

          <TextField
            label={i18n.t(
              'benefits.healthcare.plan_wizard.plan_details_step.basic_information.provider.label'
            )}
            mandatory
            value={data.providerName}
            onChange={(value: string) => {
              onDataChanged({ ...data, providerName: value })
            }}
            onBlur={() => markFieldTouched('providerName')}
            placeholder={i18n.t(
              'benefits.healthcare.plan_wizard.plan_details_step.basic_information.provider.placeholder'
            )}
            error={errors.providerName}
          />

          <TextField
            label={i18n.t(
              'benefits.healthcare.plan_wizard.plan_details_step.basic_information.plan_name.label'
            )}
            mandatory
            value={data.planName}
            onChange={(value: string) => {
              onDataChanged({ ...data, planName: value })
            }}
            onBlur={() => markFieldTouched('planName')}
            placeholder={i18n.t(
              'benefits.healthcare.plan_wizard.plan_details_step.basic_information.plan_name.placeholder'
            )}
            error={errors.planName}
          />

          <DayPicker
            mandatory
            label={i18n.t(
              'benefits.healthcare.plan_wizard.plan_details_step.basic_information.renewal_date.label'
            )}
            format="ui"
            value={data.renewalDate}
            onChange={(dateString: string) => {
              onDataChanged({
                ...data,
                renewalDate: dateString,
              })
              markFieldTouched('renewalDate')
            }}
            onToggle={(isOpen) => {
              if (!isOpen) {
                markFieldTouched('renewalDate')
              }
            }}
            error={errors.renewalDate}
            placeholder={i18n.t(
              'benefits.healthcare.plan_wizard.plan_details_step.basic_information.renewal_date.placeholder'
            )}
          />
        </Box>
        <Divider />

        {/* Enrollment Conditions Section */}
        <Box flexDirection="column" gap="s16">
          <Title
            title={i18n.t(
              'benefits.healthcare.plan_wizard.plan_details_step.enrollment_conditions.title'
            )}
            variant="500"
          />

          <NumberField
            label={i18n.t(
              'benefits.healthcare.plan_wizard.plan_details_step.enrollment_conditions.waiting_period.label'
            )}
            mandatory
            value={data.waitingPeriodMonths}
            onChange={(value: number | null) => {
              onDataChanged({ ...data, waitingPeriodMonths: value ?? undefined })
            }}
            onBlur={() => markFieldTouched('waitingPeriodMonths')}
            placeholder="0"
            min={0}
            max={12}
            step={1}
            error={errors.waitingPeriod}
          />

          <SelectField
            label={i18n.t(
              'benefits.healthcare.plan_wizard.plan_details_step.enrollment_conditions.pre_existing_conditions.label'
            )}
            mandatory
            value={
              data.allowPreExistingConditions === undefined
                ? ''
                : data.allowPreExistingConditions
                ? 'admitted'
                : 'not-admitted'
            }
            onChange={(value: string) => {
              if (value === '') {
                onDataChanged({ ...data, allowPreExistingConditions: undefined })
              } else {
                onDataChanged({ ...data, allowPreExistingConditions: value === 'admitted' })
              }
              markFieldTouched('allowPreExistingConditions')
            }}
            onBlur={() => markFieldTouched('allowPreExistingConditions')}
            none={i18n.t('common.select')}
            options={[
              {
                value: 'not-admitted',
                label: i18n.t(
                  'benefits.healthcare.plan_wizard.plan_details_step.enrollment_conditions.pre_existing_conditions.not_allowed'
                ),
              },
              {
                value: 'admitted',
                label: i18n.t(
                  'benefits.healthcare.plan_wizard.plan_details_step.enrollment_conditions.pre_existing_conditions.allowed'
                ),
              },
            ]}
            error={errors.allowPreExistingConditions}
          />

          {data.allowPreExistingConditions === false && (
            <Callout
              type="info"
              title={i18n.t(
                'benefits.healthcare.plan_wizard.plan_details_step.enrollment_conditions.callout.title'
              )}
              description={i18n.t(
                'benefits.healthcare.plan_wizard.plan_details_step.enrollment_conditions.callout.description'
              )}
            />
          )}
        </Box>
        <Divider />

        {/* Policy Document Section */}
        <Box flexDirection="column" gap="s16">
          <Title
            title={`${i18n.t(
              'benefits.healthcare.plan_wizard.plan_details_step.policy_document.title'
            )}`}
            variant="500"
          />

          <DropzoneBox
            onDrop={onDrop}
            onClick={() => markFieldTouched('planDocument')}
            iconName="common-file-text-upload"
            multiple={false}
            message={data.planDocument?.name || i18n.t('documents.drop_here')}
            description={i18n.t(
              'benefits.healthcare.plan_wizard.plan_details_step.policy_document.dropzone.description'
            )}
            accept={ACCEPTED_CONTENT_TYPES}
            error={!!errors.planDocument}
          />

          {errors.planDocument && (
            <Text.Normal color="radicalRed1000" size="200">
              {errors.planDocument}
            </Text.Normal>
          )}
        </Box>
      </Box>
    </Box>
  )
}

// Define step as factory function (like working wizards) instead of direct object
export const planDetailsStep = (): WizardStep<HealthcarePlanData> => ({
  title: i18n.t('benefits.healthcare.plan_wizard.plan_details_step.title'),
  render: (props) => <PlanDetailsStep {...props} />,
  nextText: i18n.t('common.continue'),
  isCompleted: (data: HealthcarePlanData) => {
    // Use Zod validation for completion check
    const validationResult = planDetailsStepSchema().safeParse(data)
    return validationResult.success
  },
})
